import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../utils/constants';
import { userAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import NotificationSettingsModal from './NotificationSettingsModal';
import { 
  Bell, 
  BellRing,
  BellOff,
  X, 
  Check, 
  CheckCheck,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Info,
  Gift,
  Megaphone,
  Settings,
  Wifi,
  WifiOff
} from 'lucide-react';

const NotificationCenter = () => {
  const { pushEnabled, pushPermission, requestPushPermission, toast, wsConnected, settings, toggleNotifications } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const dropdownRef = useRef(null);

  const loadNotifications = async () => {
    try {
      const res = await userAPI.getNotifications();
      // Handle both direct response and nested data
      const data = res.data || res;
      const notifList = data?.notifications || (Array.isArray(data) ? data : []);
      // Normalize is_read field (some use 'read', some use 'is_read')
      const normalizedList = notifList.map(n => ({
        ...n,
        is_read: n.is_read ?? n.read ?? false
      }));
      setNotifications(normalizedList);
      setUnreadCount(data?.unread_count || normalizedList.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  useEffect(() => {
    loadNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await userAPI.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await userAPI.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getIcon = (type) => {
    const iconProps = { size: 16 };
    switch (type) {
      case 'deposit':
        return <ArrowDownRight {...iconProps} color="#0ecb81" />;
      case 'withdrawal':
        return <ArrowUpRight {...iconProps} color="#f6465d" />;
      case 'trade':
      case 'position':
        return <TrendingUp {...iconProps} color="#1e90ff" />;
      case 'bonus':
      case 'promo':
        return <Gift {...iconProps} color="#f0b90b" />;
      case 'announcement':
        return <Megaphone {...iconProps} color="#8b5cf6" />;
      case 'warning':
        return <AlertCircle {...iconProps} color="#f0b90b" />;
      case 'error':
        return <AlertCircle {...iconProps} color="#f6465d" />;
      default:
        return <Info {...iconProps} color="#1e90ff" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          padding: '8px',
          backgroundColor: isOpen ? COLORS.primary + '20' : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
        }}
      >
        <Bell size={22} color={isOpen ? COLORS.primary : COLORS.textSecondary} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            width: unreadCount > 9 ? '20px' : '18px',
            height: '18px',
            backgroundColor: '#f6465d',
            borderRadius: '9px',
            fontSize: '11px',
            fontWeight: '700',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${COLORS.background}`,
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          width: '380px',
          maxHeight: '500px',
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          zIndex: 1000,
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>
                Уведомления
              </span>
              {unreadCount > 0 && (
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#f6465d',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#fff',
                }}>
                  {unreadCount} новых
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Master toggle - enable/disable all notifications */}
              <button
                onClick={toggleNotifications}
                title={settings.enabled ? 'Отключить все уведомления' : 'Включить уведомления'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px',
                  backgroundColor: settings.enabled ? `${COLORS.success}20` : `${COLORS.danger}20`,
                  border: `1px solid ${settings.enabled ? COLORS.success : COLORS.danger}`,
                  borderRadius: '6px',
                  color: settings.enabled ? COLORS.success : COLORS.danger,
                  cursor: 'pointer',
                }}
              >
                {settings.enabled ? <Bell size={14} /> : <BellOff size={14} />}
              </button>
              {/* WebSocket status indicator */}
              <div
                title={wsConnected ? 'Real-time соединение активно' : 'Real-time соединение неактивно'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px',
                  backgroundColor: wsConnected ? `${COLORS.success}20` : `${COLORS.danger}20`,
                  border: `1px solid ${wsConnected ? COLORS.success : COLORS.danger}`,
                  borderRadius: '6px',
                  color: wsConnected ? COLORS.success : COLORS.danger,
                }}
              >
                {wsConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    color: COLORS.textSecondary,
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <CheckCheck size={14} />
                  Прочитать все
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: COLORS.textSecondary,
              }}>
                <Bell size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: '14px' }}>Нет уведомлений</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    backgroundColor: notification.is_read ? 'transparent' : `${COLORS.primary}08`,
                    cursor: notification.is_read ? 'default' : 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    gap: '12px',
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: COLORS.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {getIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: COLORS.text,
                      fontSize: '14px',
                      fontWeight: notification.is_read ? '400' : '600',
                      marginBottom: '4px',
                    }}>
                      {notification.title}
                    </div>
                    {notification.message && (
                      <div style={{
                        color: COLORS.textSecondary,
                        fontSize: '13px',
                        lineHeight: '1.4',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {notification.message}
                      </div>
                    )}
                    <div style={{
                      color: COLORS.textSecondary,
                      fontSize: '11px',
                      marginTop: '6px',
                      opacity: 0.7,
                    }}>
                      {formatTime(notification.created_at)}
                    </div>
                  </div>

                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: COLORS.primary,
                      flexShrink: 0,
                      marginTop: '6px',
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '12px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              textAlign: 'center',
            }}>
              <a
                href="/notifications"
                style={{
                  color: COLORS.primary,
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                }}
              >
                Смотреть все уведомления
              </a>
            </div>
          )}
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <NotificationSettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
    </div>
  );
};

export default NotificationCenter;
